class InfoBannerSlider {
    constructor(container) {
        this.container = container;
        this.slides = container.querySelectorAll('.info-banner-slide');
        this.progressBar = container.querySelector('.info-banner-progress');
        this.currentIndex = 0;
        this.slideDuration = parseInt(container.dataset.slideDuration) || 5000;
        this.pauseOnHover = container.dataset.pauseOnHover === 'true';
        this.slidesCount = this.slides.length;

        this.isPaused = false;
        this.progressInterval = null;
        this.slideTimeout = null;
        this.startTime = null;
        this.remainingTime = this.slideDuration;

        // Drag/Swipe state
        this.isDragging = false;
        this.startX = 0;
        this.currentX = 0;
        this.dragThreshold = 50;

        if (this.slidesCount > 1) {
            this.init();
        }
    }

    init() {
        // Set up hover pause if enabled
        if (this.pauseOnHover) {
            this.container.addEventListener('mouseenter', () => this.pause());
            this.container.addEventListener('mouseleave', () => {
                if (!this.isDragging) this.resume();
            });
        }

        // Touch events for mobile
        this.container.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: true });
        this.container.addEventListener('touchmove', (e) => this.handleTouchMove(e), { passive: true });
        this.container.addEventListener('touchend', () => this.handleTouchEnd());

        // Mouse events for desktop drag
        this.container.addEventListener('mousedown', (e) => this.handleMouseDown(e));
        this.container.addEventListener('mousemove', (e) => this.handleMouseMove(e));
        this.container.addEventListener('mouseup', () => this.handleMouseUp());
        this.container.addEventListener('mouseleave', () => this.handleMouseUp());

        // Start the slider
        this.startProgress();
    }

    handleTouchStart(e) {
        this.pause(); // Pause timer on touch interaction
        this.isDragging = true;
        this.startX = e.touches[0].clientX;
    }

    handleTouchMove(e) {
        if (!this.isDragging) return;
        this.currentX = e.touches[0].clientX;
    }

    handleTouchEnd() {
        if (!this.isDragging) return;

        const diff = this.startX - this.currentX;

        // Only trigger slide change if we have a valid currentX (moved) and crossed threshold
        if (this.currentX !== 0 && Math.abs(diff) > this.dragThreshold) {
            if (diff > 0) {
                this.nextSlide();
            } else {
                this.prevSlide();
            }
        } else {
            // If no slide change, resume timer
            this.resume();
        }

        this.isDragging = false;
        this.startX = 0;
        this.currentX = 0;
    }

    handleMouseDown(e) {
        this.isDragging = true;
        this.startX = e.clientX;
        this.container.style.cursor = 'grabbing';
    }

    handleMouseMove(e) {
        if (!this.isDragging) return;
        this.currentX = e.clientX;
    }

    handleMouseUp() {
        if (!this.isDragging) return;

        const diff = this.startX - this.currentX;

        if (this.currentX !== 0 && Math.abs(diff) > this.dragThreshold) {
            if (diff > 0) {
                this.nextSlide();
            } else {
                this.prevSlide();
            }
        }

        this.isDragging = false;
        this.startX = 0;
        this.currentX = 0;
        this.container.style.cursor = '';
    }

    startProgress() {
        this.startTime = Date.now();
        this.updateProgress();

        // Schedule next slide
        this.slideTimeout = setTimeout(() => {
            this.nextSlide();
        }, this.slideDuration);
    }

    updateProgress() {
        if (this.isPaused) return;

        const elapsed = Date.now() - this.startTime;
        const progress = Math.min((elapsed / this.slideDuration) * 100, 100);

        if (this.progressBar) {
            this.progressBar.style.width = `${progress}%`;
        }

        if (progress < 100) {
            this.progressInterval = requestAnimationFrame(() => this.updateProgress());
        }
    }

    nextSlide() {
        const nextIndex = (this.currentIndex + 1) % this.slidesCount;
        this.goToSlide(nextIndex);
    }

    prevSlide() {
        const prevIndex = (this.currentIndex - 1 + this.slidesCount) % this.slidesCount;
        this.goToSlide(prevIndex);
    }

    goToSlide(index) {
        // Hide current slide
        this.slides[this.currentIndex].style.opacity = '0';
        this.slides[this.currentIndex].style.pointerEvents = 'none';

        // Show next slide
        this.currentIndex = index;
        this.slides[this.currentIndex].style.opacity = '1';
        this.slides[this.currentIndex].style.pointerEvents = 'auto';

        // Reset progress bar
        if (this.progressBar) {
            this.progressBar.style.width = '0%';
        }

        // Restart progress if not paused
        if (!this.isPaused) {
            this.startProgress();
        }
    }

    pause() {
        if (this.isPaused) return;

        this.isPaused = true;

        // Cancel ongoing animations
        if (this.progressInterval) {
            cancelAnimationFrame(this.progressInterval);
        }
        if (this.slideTimeout) {
            clearTimeout(this.slideTimeout);
        }
    }

    resume() {
        if (!this.isPaused) return;

        this.isPaused = false;

        // Reset progress bar and start fresh
        if (this.progressBar) {
            this.progressBar.style.width = '0%';
        }

        this.startProgress();
    }
}

// Initialize all sliders on the page
document.addEventListener('DOMContentLoaded', () => {
    const sliders = document.querySelectorAll('.info-banner-slider');
    sliders.forEach(slider => {
        const slidesCount = parseInt(slider.dataset.slidesCount);
        if (slidesCount > 1) {
            new InfoBannerSlider(slider);
        }
    });
});
